import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef } from 'react'
import s from './Tag.module.css'

/**
 * Tag de catégorie — le `.tag` du prototype (« Vente », « Systématique », « SYAM »)
 * et, en taille `sm`, ses pastilles compactes sans bordure (`.pill-comm` en `blue`,
 * `.pill-priv` en `violet`).
 *
 * Contrairement au `Badge` (un **état**, avec point), le tag nomme une **catégorie**.
 */
const tag = cva(s.root, {
  variants: {
    tone: {
      neutral: null,
      amber: s.toneAmber,
      blue: s.toneBlue,
      red: s.toneRed,
      violet: s.toneViolet,
      green: s.toneGreen,
    },
    size: {
      md: null,
      sm: s.sizeSm,
    },
  },
  defaultVariants: { tone: 'neutral', size: 'md' },
})

export type TagProps = ComponentPropsWithRef<'span'> & VariantProps<typeof tag>
export type TagTone = NonNullable<VariantProps<typeof tag>['tone']>

export function Tag({ tone, size, className, ...props }: TagProps) {
  return <span className={tag({ tone, size, className })} {...props} />
}

export interface TagActionProps extends ComponentPropsWithRef<'button'> {
  /** Nom accessible du bouton — l'icône n'en fournit aucun. */
  label: string
}

/**
 * L'action portée **dans** un tag — la croix de retrait des chips du prototype
 * (`anomChip` : `border:0; background:transparent; opacity:.6`).
 *
 * Transparente et en `currentcolor` : elle prend la teinte de son tag, donc le rouge d'une
 * destination non conforme comme le gris d'une origine, sans qu'aucun ton n'ait à être
 * redéclaré ici.
 *
 * ⚠️ Elle vit dans `@ghp/ui` et non dans le module CSS d'un écran : c'est une **chose stylée**,
 * et la première qu'un second écran redessinerait de travers. Sa cible tactile reste celle du
 * tag qui la porte — c'est un geste de bureau, sur un écran d'administration.
 */
export function TagAction({ label, className, type, ...props }: TagActionProps) {
  return (
    <button
      type={type ?? 'button'}
      aria-label={label}
      className={className === undefined ? s.action : `${s.action} ${className}`}
      {...props}
    />
  )
}
